import React from 'react';
import { CalendarClock } from 'lucide-react';
import { SectionCard } from '../../common/SectionCard';
import { GateStatusPill, DocumentStatusBadge } from '../../common/StatusPills';
import { Progress } from '../../ui/Progress';
import { EmptyState } from '../../common/EmptyState';
import { formatDate, daysUntil } from '../../../utils/format';
import type { BidDetail } from '../../../types/domain';
export function BidPanel({
  bid
}: {
  bid?: BidDetail;
}) {
  if (!bid) {
    return <EmptyState title="No bid dossier yet" description="Bid detail is created when the record clears the Assessment gate and the host-body requirement set is applied." data-id="element-866" data-mp="ua13dc3hrp18" />;
  }
  const days = daysUntil(bid.submissionDeadline);
  return <div className="space-y-4" data-id="element-867" data-mp="laxj44sl93l5">
      <SectionCard title={`${bid.hostBody} requirement matrix`} description="Mandatory and supporting requirements evidenced against the host-body specification" actions={<div className="flex items-center gap-3" data-id="element-869" data-mp="25gn8d6l05wmn">
            <div className="w-28" data-id="element-870" data-mp="25eu1bq6h9ae4">
              <Progress value={bid.readiness} data-id="element-871" data-mp="2afgn12c0ztoe" />
            </div>
            <span className="font-mono text-[12px] text-muted-foreground" data-id="element-872" data-mp="2ah1h6qufhalq">
              {bid.readiness}% ready
            </span>
          </div>} bodyClassName="p-0" data-id="element-868" data-mp="2klxa52o8rngs">
        <ul className="divide-y divide-border" data-id="element-873" data-mp="21fl9wrywtolq">
          {bid.requirements.map(requirement => <li key={requirement.id} className="flex items-start justify-between gap-3 px-4 py-2.5" data-id="element-874" data-mp="2p4wc8dvwrr1z">
              <div className="min-w-0" data-id="element-875" data-mp="2feku9nfe3hc3">
                <p className="text-[13px] text-foreground" data-id="element-876" data-mp="2fdi8ftwxc531">{requirement.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground" data-id="element-877" data-mp="2kcure3kocs0c">{requirement.note}</p>
              </div>
              <GateStatusPill status={requirement.status} data-id="element-878" data-mp="38278rqq5o6dq" />
            </li>)}
        </ul>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2" data-id="element-879" data-mp="3cvi5v05618sg">
        <SectionCard title="Bid dossier" description="Technical volumes and supporting documents" bodyClassName="p-0" data-id="element-880" data-mp="3mpnrs50qcqu1">
          <ul className="divide-y divide-border" data-id="element-881" data-mp="3hrm75fnnojgg">
            {bid.dossier.map(item => <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-882" data-mp="3cpwxa3c66bty">
                <div className="min-w-0" data-id="element-883" data-mp="37sewx1c7x4xk">
                  <p className="truncate text-[13px]" data-id="element-884" data-mp="33qtel0gwzyfz">{item.label}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-885" data-mp="33t65420qnh4n">
                    Updated {formatDate(item.updatedOn)}
                  </p>
                </div>
                <DocumentStatusBadge status={item.status} data-id="element-886" data-mp="2u21rp6qpo4bx" />
              </li>)}
          </ul>
        </SectionCard>

        <div className="space-y-4" data-id="element-887" data-mp="2p02mdrbeuxcc">
          <SectionCard title="Submission deadline" data-id="element-888" data-mp="2k3ly8x47oiz9">
            <div className="flex items-center gap-3" data-id="element-889" data-mp="2k2arqpjdsx4g">
              <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-muted" data-id="element-890" data-mp="2g18rq6hn4h1f">
                <CalendarClock className="h-5 w-5 text-uae-green" aria-hidden="true" data-id="element-891" data-mp="vk7zmj4kyywv" />
              </span>
              <div data-id="element-892" data-mp="zka04oykxmak">
                <p className="text-sm font-semibold" data-id="element-893" data-mp="zjgsllrsqsrd">{formatDate(bid.submissionDeadline)}</p>
                <p className="text-[12px] text-muted-foreground" data-id="element-894" data-mp="14lfvyvnjdpob">
                  {days >= 0 ? `${days} days remaining in the submission window` : `Submitted — window closed ${Math.abs(days)} days ago`}
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Guarantee & commitment letters" bodyClassName="p-0" data-id="element-895" data-mp="gsh9v2awaf6j">
            <ul className="divide-y divide-border" data-id="element-896" data-mp="qkd8qfkt29bt">
              {bid.guarantees.map(guarantee => <li key={guarantee.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-897" data-mp="3pade1uiwp7p5">
                  <div className="min-w-0" data-id="element-898" data-mp="3u7vqvw01o72f">
                    <p className="truncate text-[13px]" data-id="element-899" data-mp="1zsyn9fq4xrz8">{guarantee.label}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-900" data-mp="1zqvnw533emhu">{guarantee.issuer}</p>
                  </div>
                  <GateStatusPill status={guarantee.status} data-id="element-901" data-mp="29j1fhdpfn1wi" />
                </li>)}
            </ul>
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Competing host cities" description="Relative strength assessment against declared competing bids" bodyClassName="p-0" data-id="element-902" data-mp="24qrpjxobi1u8">
        <ul className="divide-y divide-border" data-id="element-903" data-mp="2eh2yxzqrt38i">
          {bid.competitors.map(competitor => <li key={competitor.city} className="px-4 py-3" data-id="element-904" data-mp="2sguieg023btw">
              <div className="flex items-center justify-between gap-3" data-id="element-905" data-mp="2nj2esoq1u19v">
                <p className="text-[13px] font-medium" data-id="element-906" data-mp="2ngpy0ew11wye">
                  {competitor.city}
                  <span className="ml-1.5 text-[12px] font-normal text-muted-foreground" data-id="element-907" data-mp="y44kln6khih8">
                    {competitor.country}
                  </span>
                </p>
                <span className="font-mono text-[12px] text-muted-foreground" data-id="element-908" data-mp="34tg1ld8l1crn">
                  {competitor.strength}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted" data-id="element-909" data-mp="2w02zplsjj0tf">
                <div className="h-full rounded-full bg-uae-green" style={{
              width: `${competitor.strength}%`
            }} data-id="element-910" data-mp="301es1z2su05b" />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-911" data-mp="2m401us4q1oca">{competitor.note}</p>
            </li>)}
        </ul>
      </SectionCard>
    </div>;
}